import { AddInError, isMissingFolderMessage } from '@utils-global';

/**
 * EWS ResponseCodes that are safe to retry. All but the last are flagged by the
 * Exchange docs as transient — "try your request again later", server busy, or
 * a temporarily unavailable mailbox/store. ErrorItemNotFound is included because
 * the item may simply not be synced yet (replication lag), mirroring how the
 * Graph path treats it.
 * https://learn.microsoft.com/exchange/client-developer/web-service-reference/responsecode
 */
const TRANSIENT_EWS_RESPONSE_CODES = [
  'ErrorADUnavailable',
  'ErrorInsufficientResources',
  'ErrorInternalServerTransientError',
  'ErrorMailboxMoveInProgress',
  'ErrorMailboxStoreUnavailable',
  'ErrorServerBusy',
  'ErrorTimeoutExpired',
  'ErrorItemNotFound',
];

/**
 * Raised when an EWS request completes but the SOAP body reports a fault
 * (`ResponseCode` other than `NoError`). `makeEwsRequestAsync` reports these as
 * a successful async result, so callers must inspect the body to notice them.
 */
export class EwsResponseError extends AddInError {
  public readonly responseCode: string;

  public readonly retryable: boolean;

  constructor(
    operation: string,
    responseCode: string,
    messageText?: string,
    destinationFolderId?: string,
  ) {
    const missingFolder = isMissingFolderMessage(messageText, destinationFolderId);

    super(`${operation} failed (EWS response code: ${responseCode})`, {
      responseCode,
      messageText,
      // Kept on the context so the UI reads a decided flag rather than re-reading the fault text.
      ...(missingFolder && { missingFolder }),
    });
    this.name = 'EwsResponseError';
    this.responseCode = responseCode;
    // A missing destination folder arrives as ErrorItemNotFound too, but describes the mailbox
    // lacking the folder we asked to move to rather than an item that may still show up. Scoped
    // to that one code: a busy or moving mailbox keeps its retries even if its MessageText
    // happens to name a folder, since that store does come back.
    this.retryable =
      TRANSIENT_EWS_RESPONSE_CODES.includes(responseCode) &&
      !(responseCode === 'ErrorItemNotFound' && missingFolder);
    Object.setPrototypeOf(this, EwsResponseError.prototype);
  }
}

/**
 * Whether a parsed EWS `ResponseCode` represents a fault. `NoError` is the
 * success value of the EWS ResponseCode enumeration; anything else (or a
 * missing code) is an error we must surface rather than treat as success.
 * https://learn.microsoft.com/exchange/client-developer/web-service-reference/responsecode
 */
export const isEwsResponseFault = (responseCode: string | null): responseCode is string =>
  !!responseCode && responseCode !== 'NoError';

/**
 * A transport-level failure (no response code) is transient by nature; a SOAP
 * fault is retryable only when its response code is transient.
 */
export const isRetryableEwsError = (error: unknown): boolean =>
  error instanceof EwsResponseError ? error.retryable : true;
